<template>
  <div 
    v-if="showMenu && editor"
    class="video-bubble-menu"
    :style="menuStyle"
    @mouseenter="handleMenuEnter"
    @mouseleave="handleMenuLeave"
  >
    <div class="bubble-menu-content">
      <!-- Display Mode -->
      <div class="menu-group">
        <button
          :class="['menu-btn', { active: isDisplay('block') }]"
          @click="setDisplay('block')"
          title="Block Display"
        >
          <BlockDisplay />
        </button>
        <button
          :class="['menu-btn', { active: isDisplay('inline') }]"
          @click="setDisplay('inline')"
          title="Inline Display"
        >
          <InlineDisplay />
        </button>
      </div>

      <div class="bbl-menu-divider"></div>

      <!-- Alignment Dropdown -->
      <BubbleMenuDropdown :items="alignmentItems">
        <template #trigger>
          <component :is="currentAlignIcon" />
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Spacing Dropdown (only show for left/right alignment in block mode) -->
      <BubbleMenuDropdown v-if="showSpacing" :items="spacingItems">
        <template #trigger>
          <component :is="currentSpacingIcon" width="20" height="20"/>
        </template>
      </BubbleMenuDropdown>

      <div v-if="showSpacing" class="bbl-menu-divider"></div>

      <!-- Caption Dropdown -->
      <BubbleMenuDropdown :items="captionItems">
        <template #trigger>
          <component :is="currentCaptionIcon" width="20" height="20"/>
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Delete -->
      <button
        class="menu-btn delete-btn"
        @click="deleteVideo"
        title="Delete Video"
      >
        <Delete />
      </button>
    </div>
  </div>
</template>

<script>
import { 
  LeftAlign, 
  CenterAlign, 
  RightAlign,
  BlockDisplay,
  InlineDisplay,
  CaptionTop,
  CaptionBottom,
  NoCaption,
  Delete,
  SpacingSmall,
  SpacingMedium,
  SpacingLarge
} from '../svg'
import BubbleMenuDropdown from '../Toolbar/BubbleMenuDropdown.vue'

export default {
  name: 'VideoBubbleMenu',
  components: {
    LeftAlign,
    CenterAlign,
    RightAlign,
    BlockDisplay,
    InlineDisplay,
    CaptionTop,
    CaptionBottom,
    NoCaption,
    Delete,
    SpacingSmall,
    SpacingMedium,
    SpacingLarge,
    BubbleMenuDropdown
  },
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showMenu: false,
      menuStyle: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: 1000
      },
      currentVideoElement: null,
      hideTimeout: null
    }
  },
  mounted() {
    this.attachHoverListeners()
  },
  beforeDestroy() {
    this.removeHoverListeners()
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
  },
  computed: {
    showSpacing() {
      if (!this.editor) return false
      const attrs = this.editor.getAttributes('resizableVideo')
      const display = attrs.display || 'block'
      const align = attrs.align || 'left'
      
      // Show spacing controls only for block mode with left/right alignment
      return display === 'block' && (align === 'left' || align === 'right')
    },
    
    currentAlignIcon() {
      const align = this.editor.getAttributes('resizableVideo').align || 'left'
      return align === 'center' ? CenterAlign : align === 'right' ? RightAlign : LeftAlign
    },
    
    currentSpacingIcon() {
      const spacing = this.editor.getAttributes('resizableVideo').spacing || 'medium'
      return spacing === 'small' ? SpacingSmall : spacing === 'large' ? SpacingLarge : SpacingMedium
    },
    
    currentCaptionIcon() {
      const position = this.editor.getAttributes('resizableVideo').captionPosition || 'none'
      return position === 'top' ? CaptionTop : position === 'bottom' ? CaptionBottom : NoCaption
    },
    
    alignmentItems() {
      return [
        {
          label: 'Align Left',
          icon: LeftAlign,
          action: () => this.setAlign('left'),
          active: this.isAlign('left')
        },
        {
          label: 'Align Center',
          icon: CenterAlign,
          action: () => this.setAlign('center'),
          active: this.isAlign('center')
        },
        {
          label: 'Align Right',
          icon: RightAlign,
          action: () => this.setAlign('right'),
          active: this.isAlign('right')
        }
      ]
    },
    
    spacingItems() {
      return [
        {
          label: 'Small Spacing',
          icon: SpacingSmall,
          action: () => this.setSpacing('small'),
          active: this.isSpacing('small')
        },
        {
          label: 'Medium Spacing',
          icon: SpacingMedium,
          action: () => this.setSpacing('medium'),
          active: this.isSpacing('medium')
        },
        {
          label: 'Large Spacing',
          icon: SpacingLarge,
          action: () => this.setSpacing('large'),
          active: this.isSpacing('large')
        }
      ]
    },
    
    captionItems() {
      return [
        {
          label: 'No Caption',
          icon: NoCaption,
          action: () => this.setCaptionPosition('none'),
          active: this.isCaptionPosition('none')
        },
        {
          label: 'Caption Top',
          icon: CaptionTop,
          action: () => this.setCaptionPosition('top'),
          active: this.isCaptionPosition('top')
        },
        {
          label: 'Caption Bottom',
          icon: CaptionBottom,
          action: () => this.setCaptionPosition('bottom'),
          active: this.isCaptionPosition('bottom')
        }
      ]
    }
  },
  methods: {
    attachHoverListeners() {
      if (!this.editor || !this.editor.view) return
      
      const editorElement = this.editor.view.dom
      
      // Use event delegation for hover
      editorElement.addEventListener('mouseover', this.handleVideoHover)
      editorElement.addEventListener('mouseout', this.handleVideoLeave)
    },
    
    removeHoverListeners() {
      if (!this.editor || !this.editor.view) return
      
      const editorElement = this.editor.view.dom
      editorElement.removeEventListener('mouseover', this.handleVideoHover)
      editorElement.removeEventListener('mouseout', this.handleVideoLeave)
    },
    
    handleVideoHover(event) {
      // Find if we're hovering over a video container
      const videoContainer = event.target.closest('.resizable-video-container')
      
      if (videoContainer) {
        if (this.hideTimeout) {
          clearTimeout(this.hideTimeout)
          this.hideTimeout = null
        }
        
        this.currentVideoElement = videoContainer
        this.positionMenu(videoContainer)
        this.showMenu = true
        
        // Focus the video node in editor
        this.focusVideoNode(videoContainer)
      }
    },
    
    handleVideoLeave(event) {
      const videoContainer = event.target.closest('.resizable-video-container')
      
      if (videoContainer && !(event.relatedTarget && event.relatedTarget.closest('.video-bubble-menu'))) {
        // Delay hiding to allow moving to menu
        this.hideTimeout = setTimeout(() => {
          this.showMenu = false
          this.currentVideoElement = null
        }, 200)
      }
    },
    
    handleMenuEnter() {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout)
        this.hideTimeout = null
      }
    },
    
    handleMenuLeave() {
      this.hideTimeout = setTimeout(() => {
        this.showMenu = false
        this.currentVideoElement = null
      }, 200)
    },
    
    positionMenu(videoContainer) {
      const rect = videoContainer.getBoundingClientRect()
      
      // Position menu above the video
      this.menuStyle = {
        position: 'fixed',
        top: `${rect.top - 50}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: 'translateX(-50%)',
        zIndex: 1000
      }
    },
    
    focusVideoNode(videoContainer) {
      // Find the position of this video in the document
      const { state, view } = this.editor
      const { doc } = state
      
      let videoPos = null
      doc.descendants((node, pos) => {
        if (node.type.name === 'resizableVideo') {
          const domNode = view.nodeDOM(pos)
          if (domNode === videoContainer || (domNode && domNode.contains(videoContainer))) {
            videoPos = pos
            return false
          }
        }
      })
      
      if (videoPos !== null) {
        // Set selection to the video node
        const tr = state.tr.setSelection(
          this.editor.state.selection.constructor.create(state.doc, videoPos)
        )
        view.dispatch(tr)
      }
    },
    
    isDisplay(display) {
      return this.editor.isActive('resizableVideo', { display })
    },
    
    isAlign(align) {
      return this.editor.isActive('resizableVideo', { align })
    },
    
    isCaptionPosition(position) {
      return this.editor.isActive('resizableVideo', { captionPosition: position })
    },
    
    isSpacing(spacing) {
      return this.editor.isActive('resizableVideo', { spacing })
    },
    
    setDisplay(display) {
      this.editor.commands.updateAttributes('resizableVideo', { display })
    },
    
    setAlign(align) {
      this.editor.commands.updateAttributes('resizableVideo', { align })
    },
    
    setCaptionPosition(position) {
      const currentCaption = this.editor.getAttributes('resizableVideo').caption || ''
      const newCaption = position === 'none' ? '' : (currentCaption || 'Enter caption...')
      
      this.editor.commands.updateAttributes('resizableVideo', { 
        captionPosition: position,
        caption: newCaption
      })
    },
    
    setSpacing(spacing) {
      this.editor.commands.updateAttributes('resizableVideo', { spacing })
    },
    
    deleteVideo() {
      this.editor.commands.deleteSelection()
      this.showMenu = false
    }
  }
}
</script>

<style scoped>
.video-bubble-menu {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px;
  display: flex;
  gap: 4px;
}

.bubble-menu-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.menu-group {
  display: flex;
  gap: 2px;
}

.bbl-menu-divider {
  width: 1px;
  height: 24px;
  background: #e5e7eb;
  margin: 0 4px;
}

.menu-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  transition: all 0.15s ease;
  padding: 6px;
}

.menu-btn:hover {
  background: #f3f4f6;
}

.menu-btn.active {
  background: #dbeafe;
  color: #2563eb;
}

.menu-btn svg {
  width: 18px;
  height: 18px;
}

.delete-btn {
  color: #dc2626;
}

.delete-btn:hover {
  background: #fee2e2;
}
</style>
